import * as React from 'react';

import * as ToggleGroupPrimitive from '@radix-ui/react-toggle-group';
import { tv, type VariantProps } from 'tailwind-variants';

import CloseIcon from '@mui/icons-material/Close';

import cn from '../../utils/cn';

const chipVariants = tv({
  base: 'flex items-center justify-center rounded-2xl border border-gray-light bg-white px-3.5 text-xs font-medium text-gray-dark transition-colors duration-200 hover:bg-white active:scale-95 active:ring-0 active:transition-all disabled:active:scale-100',
  variants: {
    variant: {
      default: '',
      outline: 'border-gray-light bg-transparent hover:bg-transparent',
    },
    color: {
      default:
        'hover:border-primary-dark hover:text-primary-dark data-[state=on]:border-primary-dark data-[state=on]:bg-primary-light/10 data-[state=on]:text-primary-dark',
      primary:
        'hover:border-primary-dark hover:text-primary-dark data-[state=on]:border-primary-dark data-[state=on]:bg-primary-light/10 data-[state=on]:text-primary-dark',
      secondary:
        'hover:border-secondary-dark hover:text-secondary-dark data-[state=on]:border-secondary-dark data-[state=on]:bg-secondary-light/10 data-[state=on]:text-secondary-dark',
      danger:
        'hover:border-danger-dark hover:text-danger-dark data-[state=on]:border-danger-dark data-[state=on]:bg-danger-light/10 data-[state=on]:text-danger-dark',
      warning:
        'hover:border-warning-dark hover:text-warning-dark data-[state=on]:border-warning-dark data-[state=on]:bg-warning-light/10 data-[state=on]:text-warning-dark',
      success:
        'hover:border-success-dark hover:text-success-dark data-[state=on]:border-success-dark data-[state=on]:bg-success-light/10 data-[state=on]:text-success-dark',
    },
    size: {
      regular: 'h-8 py-1',
      condensed: 'h-6 py-0.5',
    },
    disabled: {
      true: 'cursor-not-allowed border-gray-light opacity-50 hover:border-gray-light  hover:text-gray-dark',
      false: '',
    },
  },
  defaultVariants: {
    color: 'default',
    variant: 'default',
    size: 'regular',
    disabled: false,
  },
});

export type ChipProps = VariantProps<typeof chipVariants>;

const ChipGroupContext = React.createContext<VariantProps<typeof chipVariants>>(
  {
    size: 'regular',
    variant: 'default',
    disabled: false,
    color: 'default',
  }
);

const ChipProvider = React.forwardRef<
  React.ElementRef<typeof ToggleGroupPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof ToggleGroupPrimitive.Root> &
    VariantProps<typeof chipVariants>
>(({ className, color, variant, size, disabled, children, ...props }, ref) => (
  <ToggleGroupPrimitive.Root
    ref={ref}
    className={cn('flex items-center justify-center gap-1', className)}
    {...props}
  >
    <ChipGroupContext.Provider value={{ variant, size, disabled, color }}>
      {children}
    </ChipGroupContext.Provider>
  </ToggleGroupPrimitive.Root>
));

ChipProvider.displayName = ToggleGroupPrimitive.Root.displayName;

const Chip = React.forwardRef<
  React.ElementRef<typeof ToggleGroupPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof ToggleGroupPrimitive.Item> &
    VariantProps<typeof chipVariants> & {
      icon?: React.ReactNode;
      removeIcon?: 'show' | 'hide';
    }
>(
  (
    {
      className,
      children,
      color,
      variant,
      icon,
      size,
      removeIcon,
      disabled,
      ...props
    },
    ref
  ) => {
    const context = React.useContext(ChipGroupContext);

    const customIcon = (newIcon: React.ReactNode) => {
      if (!newIcon) return null;
      return React.cloneElement(newIcon as React.ReactElement, {
        className: cn(
          (newIcon as React.ReactElement)?.props?.className || 'size-4 mr-2'
        ),
      });
    };

    return (
      <ToggleGroupPrimitive.Item
        ref={ref}
        disabled={context.disabled || disabled}
        className={cn(
          chipVariants({
            color: context.color || color,
            variant: context.variant || variant,
            size: context.size || size,
            disabled: context.disabled || disabled,
          }),
          className
        )}
        {...props}
      >
        {customIcon(icon)} {children}{' '}
        {removeIcon === 'show' && (
          <CloseIcon data-testid='remove-icon' className='-mr-1 ml-1 size-4' />
        )}
        {removeIcon === 'hide' && (
          <span
            data-testid='remove-icon-placeholder'
            className='-mr-1 ml-1 size-4'
          />
        )}
      </ToggleGroupPrimitive.Item>
    );
  }
);

Chip.displayName = ToggleGroupPrimitive.Item.displayName;

export { ChipProvider };

export default Chip;
